import { State } from "@/src/modeler/state";
import { ClickMeID, useClickMe } from "@/components/useClickMe";
import * as d3 from "d3-hierarchy";
import {
  ArcherContainer,
  ArcherContainerRef,
  ArcherElement,
} from "react-archer";
import { DependencyList, useEffect, useRef } from "react";
import { useIsDark } from "@/src/theme/useIsDark";

const NODE_HEIGHT_REM = 6;

type Edge = { parent: string; child: string };

function spanningTree(edges: Edge[]): Edge[] {
  const visited = new Set<string>();
  function dfs(node: string): Edge[] {
    visited.add(node);
    return edges
      .filter((edge) => edge.parent === node)
      .flatMap((edge) =>
        visited.has(edge.child) ? [] : [edge, ...dfs(edge.child)]
      );
  }
  return dfs("");
}

/** 60Hz */
export const REFRESH_PERIOD_MS = 1000 / 60;

const ResourceHierarchy: React.FC<{
  state: State;
  onClick?: (resource: string) => void;
  animateOnChange?: DependencyList;
  highlightedResource?: string;
}> = ({
  state: { resources },
  onClick,
  animateOnChange,
  highlightedResource,
}) => {
  const isDark = useIsDark();
  const archerContainerRef = useRef<ArcherContainerRef>(null);

  const edges: Edge[] = [
    // an edge for each relation
    ...resources.flatMap((resource) =>
      resource.relations.map((relation) => ({
        child: resource.name,
        parent: relation.resource,
      }))
    ),
    // connect all resources that access is granted to to "app"
    ...resources
      .filter((resource) => resource.isGrantedAccessTo)
      .map((resource) => ({ child: resource.name, parent: "app" })),
    // "app" is the root node
    { child: "app", parent: "" },
  ];
  // d3.stratify throws an error if the graph we give it is not a tree
  const spanningEdges = spanningTree(edges);
  const hierarchy = d3
    .stratify<Edge>()
    .id((edge) => edge.child)
    .parentId((edge) => edge.parent)(spanningEdges);
  const tree = d3.tree<Edge>().size([100, hierarchy.height])(hierarchy);

  // if the user hasn't clicked on a resource node yet,
  // we render a "click me" callout on the first granted resource
  const firstGrantedResource = resources.find((r) => r.isGrantedAccessTo)?.name;
  const { clickMe, clearClickMe } = useClickMe(
    ClickMeID.ModelerERDNode,
    "View configuration"
  );

  useEffect(() => {
    const interval = setInterval(() => {
      archerContainerRef.current?.refreshScreen();
    }, REFRESH_PERIOD_MS);
    setTimeout(() => clearInterval(interval), 300);
    return () => clearInterval(interval);
  }, animateOnChange ?? []);

  return (
    <>
      <div className="text-xl font-semibold mb-9">Model overview</div>
      <ArcherContainer
        style={{ height: `${tree.height * NODE_HEIGHT_REM}rem` }}
        strokeColor={isDark ? "#9ca3af" : "#1F2937"}
        ref={archerContainerRef}
      >
        {tree.descendants().map((node) => (
          <div
            key={node.data.child}
            style={{
              top: `${node.y * NODE_HEIGHT_REM}rem`,
              left: `${node.x}%`,
            }}
            className="absolute h-0 w-0 flex justify-center items-center"
          >
            <ArcherElement
              id={node.data.child}
              relations={edges
                .filter((edge) => edge.parent === node.data.child)
                .map((edge) => ({
                  targetId: edge.child,
                  targetAnchor: "top",
                  sourceAnchor: "bottom",
                }))}
            >
              <div
                className={`px-4 py-2 border border-border rounded shadow-md transition-all ${
                  node.data.child === highlightedResource
                    ? "bg-orange-400 border-black"
                    : "bg-card border-border"
                } ${
                  onClick &&
                  node.data.child !== "app" &&
                  node.data.child !== highlightedResource
                    ? "active:shadow-sm hover:bg-muted hover:border-border cursor-pointer relative"
                    : "cursor-default"
                }`}
                onClick={
                  onClick && node.data.child !== "app"
                    ? () => {
                        clearClickMe();
                        onClick(node.data.child);
                      }
                    : undefined
                }
              >
                {node.data.child === "app" ? "Application" : node.data.child}
                {node.data.child === firstGrantedResource && onClick && clickMe}
              </div>
            </ArcherElement>
          </div>
        ))}
      </ArcherContainer>
    </>
  );
};

export default ResourceHierarchy;
